import { useEffect, useReducer } from 'react';

import { useAppSelector } from '../../../../store/hooks';
import { getFirstPendingError } from '../../../../store/selectors/error';

type Fields = 'login' | 'pass' | 'pass1' | 'pass2' | 'app';

type Values = {
  [key in Fields]: string;
};

interface LoginState {
  values: Values;
  validation: {
    valid: boolean;
    match: boolean;
  };
  needChange: boolean;
}

interface ToggleChangePasswordFields {
  type: 'toggle';
  isActive: boolean;
}

interface ChangeFieldValue {
  type: 'change';
  field: string;
  value: any;
}

interface IUseLoginFormState {
  state: LoginState;
  change: (field: Fields) => (value: string) => void;
  toggle: (isActive: boolean) => void;
}

// TODO: Replace with types published by server
export enum LoginError {
  CHANGE_PASSWORD = 1303,
  WRONG_USERNAME_OR_PASSWORD = 1304,
  SAME_PASSWORD = 1309,
  WEAK_PASSWORD = 1310,
  REQUIRED_SMART_CARD = 1311,
  MAX_SESSIONS_EXCEEDED = 1312,
}

const reducer = (
  state: LoginState,
  action: ToggleChangePasswordFields | ChangeFieldValue
): LoginState => {
  switch (action.type) {
    case 'toggle':
      state = {
        ...state,
        values: {
          ...state.values,
          pass1:
            action.isActive !== state.needChange && !action.isActive
              ? ''
              : state.values.pass1,
          pass2:
            action.isActive !== state.needChange && !action.isActive
              ? ''
              : state.values.pass2,
        },
        needChange: action.isActive,
      };
      break;
    case 'change':
      state = {
        ...state,
        values: {
          ...state.values,
          [action.field]: action.value,
        },
      };
      break;
  }

  const match = state.values.pass1 === state.values.pass2;

  return {
    ...state,
    validation: {
      ...state.validation,
      valid:
        !!state.values.app &&
        !!state.values.pass &&
        !!state.values.login &&
        (state.needChange
          ? match && !!state.values.pass1 && !!state.values.pass2
          : true),
      match,
    },
  };
};

const useLoginFormState = (values: Values): IUseLoginFormState => {
  const [state, dispatch] = useReducer(reducer, {
    values,
    validation: {
      valid: false,
      match: false,
    },
    needChange: false,
  });

  const error = useAppSelector(getFirstPendingError);

  useEffect(() => {
    if (error?.code === LoginError.CHANGE_PASSWORD) {
      dispatch({
        type: 'toggle',
        isActive: true,
      });
    }
  }, [error?.code]);

  useEffect(() => {
    if (localStorage.getItem('app')) {
      const appName = localStorage.getItem('app') || '';
      dispatch({
        type: 'change',
        field: 'app',
        value: appName,
      });
    }
  }, []);

  const change = (field: Fields) => (value: string) => {
    dispatch({
      type: 'change',
      field,
      value,
    });
  };

  const toggle = (isActive: boolean) =>
    dispatch({
      type: 'toggle',
      isActive,
    });

  return {
    state,
    change,
    toggle,
  };
};

export default useLoginFormState;
